import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const styles = {
  container: {
    maxWidth: 900,
    margin: '40px auto',
    padding: 0,
    background: '#f6f7fb',
    borderRadius: 0,
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 0 80px 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  card: {
    background: 'linear-gradient(135deg, #fff 80%, #ffe5e5 100%)',
    borderRadius: 18,
    boxShadow: '0 6px 32px rgba(255,0,0,0.08), 0 1.5px 6px rgba(0,0,0,0.04)',
    padding: '28px 18px',
    width: '100%',
    maxWidth: 430,
    margin: '0 auto 28px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    border: '1.5px solid #f2dede',
  },
  header: {
    textAlign: 'center',
    marginBottom: 0,
  },
  logo: {
    width: 80,
    height: 'auto',
    marginBottom: 8,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(255,0,0,0.08)',
  },
  formTitle: {
    fontWeight: 900,
    fontSize: '2.2rem',
    color: '#c00',
    margin: '0 0 18px 0',
    letterSpacing: 0.5,
    lineHeight: 1.1,
  },
  section: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    padding: '18px 12px',
    marginBottom: 18,
    border: '1.5px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    marginBottom: 8,
    color: '#b00',
    letterSpacing: 0.5,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 14,
    width: '100%',
  },
  label: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 6,
    color: '#222',
    letterSpacing: 0.1,
  },
  input: {
    width: '100%',
    padding: '16px 12px',
    fontSize: 18,
    border: '1.5px solid #ccc',
    borderRadius: 10,
    background: '#f9f9f9',
    marginBottom: 0,
    boxSizing: 'border-box',
    transition: 'border 0.2s',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    minHeight: 80,
    padding: '16px 12px',
    fontSize: 18,
    border: '1.5px solid #ccc',
    borderRadius: 10,
    background: '#f9f9f9',
    marginBottom: 0,
    boxSizing: 'border-box',
    resize: 'vertical',
    transition: 'border 0.2s',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '18px 0',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 18,
    cursor: 'pointer',
    marginTop: 24,
    boxShadow: '0 4px 16px rgba(255,0,0,0.2)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  stickyFooter: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    padding: '12px 18px',
    boxShadow: '0 -2px 8px rgba(255,0,0,0.08)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
  },
};

const LOGO_URL = '/logo.jpg';

export default function Form2Screen() {
  const [formValues, setFormValues] = useState({
    projectName: '',
    projectReference: '',
    projectSummary: '',
    siteAddress: '',
    startDate: '',
    finishDate: '',
    issueDate: '',
    documentNumber: '',
    mainContactNumber: '',
    supervisorName: '',
    supervisorContactNumber: '',
    healthSafetyName1: '',
    healthSafetyContact1: '',
    healthSafetyName2: '',
    healthSafetyContact2: '',
    firstAider: '',
    firstAidBoxLocation: '',
    nearestHospital: '',
    evacuationPoint: '',
    permit1: '',
    permit2: '',
    additionalTraining: '',
    equipmentNeeded: '',
    hardHat: '',
    safetyBoots: '',
    hiViz: '',
    safetyGloves: '',
    eyeProtection: '',
    hearingProtection: '',
    nitrileGloves: '',
    respiratoryProtection: '',
    wasteDisposal: '',
    orderOfOperations: '',
    otherInformation: '',
  });

  const pdfRef = useRef();

  const handleInputChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generatePDF();
  };

  const generatePDF = async () => {
    try {
    const element = pdfRef.current;
      
      // Generate PDF as blob
      const pdfBlob = await html2pdf()
      .set({
        margin: 0.5,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      })
      .from(element)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Method_Statement_Form_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'form2',
        title: 'Method Statement Form',
        location: formValues.siteAddress || 'Not specified',
        createdBy: formValues.supervisorName || 'Not specified',
        projectName: formValues.projectName || 'Not specified',
        projectReference: formValues.projectReference || 'Not specified',
        startDate: formValues.startDate || 'Not specified',
        finishDate: formValues.finishDate || 'Not specified',
        issueDate: formValues.issueDate || currentDate,
        documentNumber: formValues.documentNumber || 'Not specified',
        mainContactNumber: formValues.mainContactNumber || 'Not specified',
        supervisorContactNumber: formValues.supervisorContactNumber || 'Not specified',
        healthSafetyName1: formValues.healthSafetyName1 || 'Not specified',
        healthSafetyContact1: formValues.healthSafetyContact1 || 'Not specified',
        healthSafetyName2: formValues.healthSafetyName2 || 'Not specified',
        healthSafetyContact2: formValues.healthSafetyContact2 || 'Not specified',
        firstAider: formValues.firstAider || 'Not specified',
        firstAidBoxLocation: formValues.firstAidBoxLocation || 'Not specified',
        nearestHospital: formValues.nearestHospital || 'Not specified',
        evacuationPoint: formValues.evacuationPoint || 'Not specified',
        permit1: formValues.permit1 || 'Not specified',
        permit2: formValues.permit2 || 'Not specified',
        additionalTraining: formValues.additionalTraining || 'Not specified',
        equipmentNeeded: formValues.equipmentNeeded || 'Not specified',
        hardHat: formValues.hardHat || 'Not specified',
        safetyBoots: formValues.safetyBoots || 'Not specified',
        hiViz: formValues.hiViz || 'Not specified',
        safetyGloves: formValues.safetyGloves || 'Not specified',
        eyeProtection: formValues.eyeProtection || 'Not specified',
        hearingProtection: formValues.hearingProtection || 'Not specified',
        nitrileGloves: formValues.nitrileGloves || 'Not specified',
        respiratoryProtection: formValues.respiratoryProtection || 'Not specified',
        wasteDisposal: formValues.wasteDisposal || 'Not specified',
        orderOfOperations: formValues.orderOfOperations || 'Not specified',
        otherInformation: formValues.otherInformation || 'Not specified'
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={styles.container} className="form2-container">
      <style>{`
        @media (max-width: 600px) {
          .form2-container {
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: #f6f7fb !important;
          }
          .form2-scroll {
            padding: 12px 0 90px 0 !important;
          }
          .form2-card {
            padding: 18px 6px !important;
            border-radius: 12px !important;
            margin-bottom: 18px !important;
            max-width: 100vw !important;
          }
          .form2-logo {
            width: 60px !important;
            margin-bottom: 4px !important;
          }
          .form2-title {
            font-size: 1.3rem !important;
          }
          .form2-formTitle {
            font-size: 1.5rem !important;
          }
          .form2-sectionTitle {
            font-size: 1.05rem !important;
          }
          .form2-label {
            font-size: 13px !important;
            margin-bottom: 3px !important;
          }
          .form2-button {
            font-size: 16px !important;
            padding: 14px 0 !important;
            border-radius: 10px !important;
            max-width: 100vw !important;
          }
          .form2-input, .form2-textarea {
            font-size: 16px !important;
            padding: 12px !important;
            border-radius: 8px !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100vw !important;
          }
          .form2-stickyFooter {
            position: fixed !important;
            left: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            border-radius: 0 !important;
            z-index: 1000 !important;
            box-shadow: 0 -2px 8px rgba(255,0,0,0.08) !important;
            background: linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%) !important;
            padding: 0 !important;
            margin: 0 !important;
            display: flex !important;
            justify-content: center !important;
          }
        }
        @media (max-width: 400px) {
          .form2-title {
            font-size: 1.1rem !important;
          }
          .form2-formTitle {
            font-size: 1.1rem !important;
          }
          .form2-sectionTitle {
            font-size: 0.95rem !important;
          }
          .form2-label {
            font-size: 11px !important;
          }
        }
      `}</style>
      <div style={styles.scrollArea} className="form2-scroll">
        <div style={styles.card} className="form2-card">
          <div style={styles.header}>
            <img src={LOGO_URL} alt="Logo" style={styles.logo} className="form2-logo" />
            <div style={styles.formTitle} className="form2-formTitle">Method Statement</div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Project Summary</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Project Name</label>
              <input style={styles.input} className="form2-input" name="projectName" value={formValues.projectName} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Project Reference</label>
              <input style={styles.input} className="form2-input" name="projectReference" value={formValues.projectReference} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Project Summary</label>
              <input style={styles.input} className="form2-input" name="projectSummary" value={formValues.projectSummary} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Site Address</label>
              <input style={styles.input} className="form2-input" name="siteAddress" value={formValues.siteAddress} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Start Date</label>
              <input style={styles.input} className="form2-input" name="startDate" value={formValues.startDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Finish Date</label>
              <input style={styles.input} className="form2-input" name="finishDate" value={formValues.finishDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Issue Date</label>
              <input style={styles.input} className="form2-input" name="issueDate" value={formValues.issueDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Document Number</label>
              <input style={styles.input} className="form2-input" name="documentNumber" value={formValues.documentNumber} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Contact Information</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Main Contact Number</label>
              <input style={styles.input} className="form2-input" name="mainContactNumber" value={formValues.mainContactNumber} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Supervisor Name</label>
              <input style={styles.input} className="form2-input" name="supervisorName" value={formValues.supervisorName} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Supervisor Contact Number</label>
              <input style={styles.input} className="form2-input" name="supervisorContactNumber" value={formValues.supervisorContactNumber} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Health and Safety Officer 1</label>
              <input style={styles.input} className="form2-input" name="healthSafetyName1" value={formValues.healthSafetyName1} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Health and Safety Contact Number 1</label>
              <input style={styles.input} className="form2-input" name="healthSafetyContact1" value={formValues.healthSafetyContact1} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Health and Safety Officer 2</label>
              <input style={styles.input} className="form2-input" name="healthSafetyName2" value={formValues.healthSafetyName2} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Health and Safety Contact Number 2</label>
              <input style={styles.input} className="form2-input" name="healthSafetyContact2" value={formValues.healthSafetyContact2} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">First Aider</label>
              <input style={styles.input} className="form2-input" name="firstAider" value={formValues.firstAider} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">First Aid Box Location</label>
              <input style={styles.input} className="form2-input" name="firstAidBoxLocation" value={formValues.firstAidBoxLocation} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Nearest Hospital</label>
              <input style={styles.input} className="form2-input" name="nearestHospital" value={formValues.nearestHospital} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Evacuation Point</label>
              <input style={styles.input} className="form2-input" name="evacuationPoint" value={formValues.evacuationPoint} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Permits and Training</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Permit 1</label>
              <input style={styles.input} className="form2-input" name="permit1" value={formValues.permit1} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Permit 2</label>
              <input style={styles.input} className="form2-input" name="permit2" value={formValues.permit2} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Additional Training</label>
              <input style={styles.input} className="form2-input" name="additionalTraining" value={formValues.additionalTraining} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Equipment and Protective Clothing</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Equipment Needed</label>
              <input style={styles.input} className="form2-input" name="equipmentNeeded" value={formValues.equipmentNeeded} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Hard Hat</label>
              <input style={styles.input} className="form2-input" name="hardHat" value={formValues.hardHat} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Safety Boots</label>
              <input style={styles.input} className="form2-input" name="safetyBoots" value={formValues.safetyBoots} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Hi-Viz</label>
              <input style={styles.input} className="form2-input" name="hiViz" value={formValues.hiViz} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Safety Gloves</label>
              <input style={styles.input} className="form2-input" name="safetyGloves" value={formValues.safetyGloves} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Eye Protection</label>
              <input style={styles.input} className="form2-input" name="eyeProtection" value={formValues.eyeProtection} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Hearing Protection</label>
              <input style={styles.input} className="form2-input" name="hearingProtection" value={formValues.hearingProtection} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Nitrile Gloves</label>
              <input style={styles.input} className="form2-input" name="nitrileGloves" value={formValues.nitrileGloves} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Respiratory Protection</label>
              <input style={styles.input} className="form2-input" name="respiratoryProtection" value={formValues.respiratoryProtection} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Waste Disposal Arrangements</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Hazardous substances that are identified during COSHH assessments and any other arrangements that are in place.</label>
              <textarea style={styles.textarea} className="form2-textarea" name="wasteDisposal" value={formValues.wasteDisposal} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Order of Operations</div>
            <div style={styles.field}>
              <label style={styles.label} className="form2-label">Step by step method of working/ procedure. Equipment and materials required. What hazards will be present. How to reduce risk associated.</label>
              <textarea style={styles.textarea} className="form2-textarea" name="orderOfOperations" value={formValues.orderOfOperations} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form2-sectionTitle">Other Important Information</div>
            <div style={styles.field}>
              <textarea style={styles.textarea} className="form2-textarea" name="otherInformation" value={formValues.otherInformation} onChange={handleInputChange} />
            </div>
          </div>
        </div>

        {/* Hidden PDF template */}
        <div style={{ display: 'none' }}>
          <div ref={pdfRef}>
            <html>
              <head>
                <style>
                  {`
                  body { font-family: Arial, sans-serif; }
                  h1, h2 { text-align: center; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; }
                  th, td { border: 1px solid black; padding: 8px; text-align: left; }
                  .section { margin-bottom: 20px; }
                  .section-heading { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
                  .input-value { padding: 10px; border: 1px solid #ccc; border-radius: 5px; margin-bottom: 10px; background-color: #f2f2f2; }
                  .grey-background { background-color: #f0f0f0; padding: 10px; border-radius: 5px; }
                  `}
                </style>
              </head>
              <body>
                <h1>METHOD STATEMENT</h1>
                <div style={{ textAlign: 'right', marginTop: 20 }}>
                  <img src={LOGO_URL} alt="Logo" style={{ maxWidth: '100%', width: '300px', height: 'auto' }} />
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">PROJECT SUMMARY</h2>
                  <table>
                    <tr>
                      <th>PROJECT NAME</th>
                      <td>{formValues.projectName}</td>
                    </tr>
                    <tr>
                      <th>PROJECT REFERENCE</th>
                      <td>{formValues.projectReference}</td>
                    </tr>
                    <tr>
                      <th>PROJECT SUMMARY</th>
                      <td>{formValues.projectSummary}</td>
                    </tr>
                    <tr>
                      <th>SITE ADDRESS</th>
                      <td>{formValues.siteAddress}</td>
                    </tr>
                    <tr>
                      <th>START DATE</th>
                      <td>{formValues.startDate}</td>
                    </tr>
                    <tr>
                      <th>FINISH DATE</th>
                      <td>{formValues.finishDate}</td>
                    </tr>
                    <tr>
                      <th>ISSUE DATE</th>
                      <td>{formValues.issueDate}</td>
                    </tr>
                    <tr>
                      <th>DOCUMENT NUMBER</th>
                      <td>{formValues.documentNumber}</td>
                    </tr>
                  </table>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">CONTACT INFORMATION</h2>
                  <table>
                    <tr>
                      <th>MAIN CONTACT NUMBER</th>
                      <td>{formValues.mainContactNumber}</td>
                    </tr>
                    <tr>
                      <th>SUPERVISOR NAME</th>
                      <td>{formValues.supervisorName}</td>
                    </tr>
                    <tr>
                      <th>SUPERVISOR CONTACT NUMBER</th>
                      <td>{formValues.supervisorContactNumber}</td>
                    </tr>
                    <tr>
                      <th>HEALTH AND SAFETY OFFICER 1</th>
                      <td>{formValues.healthSafetyName1}</td>
                    </tr>
                    <tr>
                      <th>HEALTH AND SAFETY CONTACT NUMBER 1</th>
                      <td>{formValues.healthSafetyContact1}</td>
                    </tr>
                    <tr>
                      <th>HEALTH AND SAFETY OFFICER 2</th>
                      <td>{formValues.healthSafetyName2}</td>
                    </tr>
                    <tr>
                      <th>HEALTH AND SAFETY CONTACT NUMBER 2</th>
                      <td>{formValues.healthSafetyContact2}</td>
                    </tr>
                    <tr>
                      <th>FIRST AIDER</th>
                      <td>{formValues.firstAider}</td>
                    </tr>
                    <tr>
                      <th>FIRST AID BOX LOCATION</th>
                      <td>{formValues.firstAidBoxLocation}</td>
                    </tr>
                    <tr>
                      <th>NEAREST HOSPITAL</th>
                      <td>{formValues.nearestHospital}</td>
                    </tr>
                    <tr>
                      <th>EVACUATION POINT</th>
                      <td>{formValues.evacuationPoint}</td>
                    </tr>
                  </table>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">PERMITS AND TRAINING</h2>
                  <table>
                    <tr>
                      <th>PERMIT 1</th>
                      <td>{formValues.permit1}</td>
                    </tr>
                    <tr>
                      <th>PERMIT 2</th>
                      <td>{formValues.permit2}</td>
                    </tr>
                    <tr>
                      <th>ADDITIONAL TRAINING</th>
                      <td>{formValues.additionalTraining}</td>
                    </tr>
                  </table>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">EQUIPMENT AND PROTECTIVE CLOTHING</h2>
                  <table>
                    <tr>
                      <th>EQUIPMENT NEEDED</th>
                      <td>{formValues.equipmentNeeded}</td>
                    </tr>
                    <tr>
                      <th>HARD HAT</th>
                      <td>{formValues.hardHat}</td>
                    </tr>
                    <tr>
                      <th>SAFETY BOOTS</th>
                      <td>{formValues.safetyBoots}</td>
                    </tr>
                    <tr>
                      <th>HI-VIZ</th>
                      <td>{formValues.hiViz}</td>
                    </tr>
                    <tr>
                      <th>SAFETY GLOVES</th>
                      <td>{formValues.safetyGloves}</td>
                    </tr>
                    <tr>
                      <th>EYE PROTECTION</th>
                      <td>{formValues.eyeProtection}</td>
                    </tr>
                    <tr>
                      <th>HEARING PROTECTION</th>
                      <td>{formValues.hearingProtection}</td>
                    </tr>
                    <tr>
                      <th>NITRILE GLOVES</th>
                      <td>{formValues.nitrileGloves}</td>
                    </tr>
                    <tr>
                      <th>RESPIRATORY PROTECTION</th>
                      <td>{formValues.respiratoryProtection}</td>
                    </tr>
                  </table>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">WASTE DISPOSAL ARRANGEMENTS</h2>
                  <p>Hazardous substances that are identified during COSHH assessments and any other arrangements that are in place.</p>
                  <p class="input-value">{formValues.wasteDisposal}</p>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">ORDER OF OPERATIONS</h2>
                  <p>Step by step method of working/ procedure. Equipment and materials required. What hazards will be present. How to reduce risk associated.</p>
                  <div class="input-value">{formValues.orderOfOperations}</div>
                </div>
                <div class="section">
                  <h2 class="section-heading grey-background">OTHER IMPORTANT INFORMATION</h2>
                  <div class="input-value">{formValues.otherInformation}</div>
                </div>
              </body>
            </html>
          </div>
        </div>

        <div style={styles.stickyFooter} className="form2-stickyFooter">
          <button style={styles.button} className="form2-button" onClick={generatePDF}>Download PDF</button>
        </div>
      </div>
    </div>
  );
}
